import { useCallback, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getWorkflows } from "@/lib/api/handlers/WorkflowHandling";
import { Workflow } from "@/types/Workflow";
import { EmailTrigger } from "@/types/enums/EmailTrigger";
import { ActionType } from "@/types/enums/ActionType";
import { Plugin } from "@/types/enums/Plugin";
import { EventContext } from "@/providers/EventProvider";
import { readGuestUpdates } from "@/lib/utils/guestUpdates";
import { GUEST_UPDATE_FIELD_WHITELIST } from "@/lib/utils/guestFieldWhitelist";

const ADDITIONAL_PREFIX = "additionalFields.";

/**
 * "Could an automation change this guest behind our back?"
 *
 * A coarse yes/no used to decide whether a surface should arm a reconcile after
 * a mutation. It deliberately does NOT evaluate workflow conditions: conditions
 * are the part the browser gets wrong, because the server's condition payload
 * carries rollups (`apptCount`, `origin`, `numCompanions`, …) that the browser
 * does not hold. Over-arming costs one wave that finds nothing; under-arming
 * leaves a stale row, which is the bug this exists to fix.
 *
 * Deliberately free of `useFields` / `useTranslations`: this is consumed by
 * data providers across several trees, and the full preview's i18n dependency
 * has no business reaching them. Prediction lives in `useGuestWorkflowPreview`.
 *
 * Costs nothing beyond what the app already fetches — the ACTIONS flag comes
 * from the event every event page already has, and the workflow list reuses the
 * automation page's query key, so react-query dedupes it across all surfaces.
 */
export interface GuestAutomationGate {
  /** False ⇒ provably nothing runs. True ⇒ maybe; arm the reconcile. */
  mayRun: (triggers: EmailTrigger[]) => boolean;
  /**
   * Which guest fields an UPDATE_GUEST pipe on these triggers could write.
   *
   * The reconcile has to watch the fields the RULES target — watching a fixed
   * pair like state/acceptanceState silently misses every rule that writes
   * anything else, and a watch that never satisfies is indistinguishable from
   * no reconcile at all.
   *
   * Deliberately over-approximate: conditions are not evaluated and every
   * target scope counts, because a field we watch needlessly costs nothing
   * while a field we miss costs the whole feature.
   */
  watchedFieldsFor: (
    triggers: EmailTrigger[],
  ) => Array<{ field: string; isAdditional: boolean }>;
  /** The event's enabled workflows, or [] while loading / denied. */
  workflows: Workflow[];
  /** True when the ACTIONS plugin is on for this event. */
  actionsPluginEnabled: boolean;
  /** True while the workflow list is still in flight. */
  loading: boolean;
  /** The list could not be read (a role without `workflows:read`) — arming is
   *  disabled and the coarse background reconcilers remain the only path. */
  denied: boolean;
}

export function useGuestAutomationGate(
  eventId: string | undefined,
): GuestAutomationGate {
  // Raw context, not `useEventContext`, so this degrades to undefined off an
  // event page instead of throwing (idiom from GuestDetailBody.tsx).
  const eventCtx = useContext(EventContext);
  const plugins = eventCtx?.event?.plugins;
  // Fail CLOSED, exactly like the consumer's `isActionsEnabled`
  // (`main-consumer/src/lib/workflow-repo.ts:31-40`): a missing event doc and a
  // non-array `plugins` are both `false` there, never "probably fine".
  const actionsPluginEnabled =
    Array.isArray(plugins) && plugins.includes(Plugin.ACTIONS);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["eventAutomationWorkflows", eventId, "workflows"],
    queryFn: () => getWorkflows(eventId as string),
    // No point asking when the plugin is off — it cannot change `mayRun`.
    enabled: !!eventId && actionsPluginEnabled,
    staleTime: 30_000,
    // A 403 must settle immediately: retrying would hold `loading` true and
    // make every mutation take the degraded path.
    retry: false,
  });

  // Stable identity: while the query loads, `data` is undefined and a bare `[]`
  // would be a fresh array each render → churns downstream memos.
  const workflows: Workflow[] = useMemo(
    () => (Array.isArray(data) ? data : []),
    [data],
  );

  /**
   * Triggers that at least one enabled workflow with an UPDATE_GUEST action
   * subscribes to. SEND_EMAIL / HTTP_REQUEST pipes are excluded on purpose:
   * they cannot change a guest row, so they are no reason to re-read one.
   */
  const triggerIndex = useMemo(() => {
    const set = new Set<EmailTrigger>();
    for (const wf of workflows) {
      if (!wf.enabled || !Array.isArray(wf.triggers)) continue;
      const writesGuest = wf.actions?.some(
        (a) => a.actionType === ActionType.UPDATE_GUEST,
      );
      if (!writesGuest) continue;
      for (const t of wf.triggers) set.add(t);
    }
    return set;
  }, [workflows]);

  const mayRun = useCallback(
    (triggers: EmailTrigger[]) => {
      if (!actionsPluginEnabled) return false;
      if (isError) return false;
      if (!Array.isArray(triggers) || triggers.length === 0) return false;
      // Not loaded yet: we cannot answer, and answering "no" would silently
      // drop the reconcile for the first mutation after a page load.
      if (data === undefined) return true;
      return triggers.some((t) => triggerIndex.has(t));
    },
    [actionsPluginEnabled, isError, data, triggerIndex],
  );

  const watchedFieldsFor = useCallback(
    (triggers: EmailTrigger[]) => {
      if (!actionsPluginEnabled || isError) return [];
      const wanted = new Set(triggers);
      const out = new Map<string, { field: string; isAdditional: boolean }>();
      for (const wf of workflows) {
        if (!wf.enabled || !Array.isArray(wf.triggers)) continue;
        if (!wf.triggers.some((t) => wanted.has(t))) continue;
        for (const action of wf.actions ?? []) {
          if (action.actionType !== ActionType.UPDATE_GUEST) continue;
          for (const u of readGuestUpdates(action.config)) {
            const bare = u.field.startsWith(ADDITIONAL_PREFIX)
              ? u.field.slice(ADDITIONAL_PREFIX.length)
              : u.field;
            // Same classification the preview uses: an unknown bare name fails
            // TOWARD additional, so a custom-field write is never missed.
            const isAdditional =
              u.field.startsWith(ADDITIONAL_PREFIX) ||
              !GUEST_UPDATE_FIELD_WHITELIST.has(u.field);
            out.set(`${bare}|${isAdditional}`, { field: bare, isAdditional });
          }
        }
      }
      return [...out.values()];
    },
    [actionsPluginEnabled, isError, workflows],
  );

  return {
    mayRun,
    watchedFieldsFor,
    workflows,
    actionsPluginEnabled,
    loading: actionsPluginEnabled && isLoading,
    denied: isError,
  };
}
